import { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Box,
  Switch,
  Button,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import BatchProcessManagement from "./BatchProcessManagement";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import FailedProcesses from "./FailedProcesses";
import { ETLSequencesEndPointLogic } from "../../../../../logic/Admin/ETLSequences/ETLSequencesEndPointLogic";
import { useDispatch, useSelector } from "react-redux";

interface Props {
  etlSequence: any;
  setEtlSequence: any;
  oauthTokenHeader: string;
  onChainStatusChange: any;
  selectedSequenceId: any;
}

const SequenceTableAdditionalContent = ({
  etlSequence,
  setEtlSequence,
  oauthTokenHeader,
  onChainStatusChange,
  selectedSequenceId,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const failedLogs = useSelector(
    (state: any) => state.etlSeq?.failedLogs?.data || [],
  );
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  const [etlChainContent, setETLChainContent] = useState({});
  const [fetchDataCalled, setFetchDataCalled] = useState(false);
  const [loadingFL, setLoadingFL] = useState(false);
  const isSwitchChecked = false;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const updatedChainStatus: boolean = Object.values(
    etlChainContent,
  )[1] as boolean;
  const [updatedPayload, setUpdatedPayload] = useState({ processes: [] } as {
    processes: any[];
  });
  const originalProcesses = [
    ...updatedPayload.processes?.map((process) => process.process__id),
  ];
  const [changesSaved, setChangesSaved] = useState(false); // To check if changes are saved

  const handleProcessChange = () => {
    setChangesSaved(false);
  };

  const handleChainStatusUpdate = useCallback(() => {
    onChainStatusChange(updatedChainStatus);
  }, [onChainStatusChange, updatedChainStatus]);

  useEffect(() => {
    handleChainStatusUpdate();
  }, [handleChainStatusUpdate, updatedChainStatus]);

  const handleSwitchChange = async (rowId: any) => {
    setLoading(true);
    let data = "";
    try {
      await ETLSequencesEndPointLogic.etlSequenceToggle(
        oauthTokenHeader,
        setETLChainContent,
        data,
        rowId,
      );
      setSnackbarOpen(true);
      setSnackbarMessage(
        "Sequence was successfully updated and changes were deployed.",
      );
      if (oauthTokenHeader) {
        setLoading(true);
        await ETLSequencesEndPointLogic.getETLSequenceContent(
          oauthTokenHeader,
          setEtlSequence,
          dispatch,
        );
      } else {
        throw new Error(
          "User doesn't have permissions to fetch ETL Sequence data",
        );
      }
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarMessage("Error while toggling sequence status");
      setErrorAlert("Error while toggling sequence status");
      console.error("Error while toggling sequence status:", error);
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
      setSnackbarMessage(
        "Sequence was successfully updated and changes were deployed.",
      );
    }
  };

  useEffect(() => {
    if (!loading && fetchDataCalled) {
      setSnackbarOpen(true);
      setSnackbarMessage(
        `Sequence was successfully updated and changes were deployed.`,
      );
    }
  }, [loading, fetchDataCalled]);

  const processss = etlSequence?.data?.find(
    (sequence: any) => sequence?.id === selectedSequenceId,
  );
  const processlist = processss?.processes || [];
  //const failedProcesses = processss?.failed_processes || [];
  const failedProcesses =
    failedLogs?.map((item: any, index: number) => ({
      order: index + 1,
      process__name: item.process_name,
      end_time: item.end_time,
    })) || [];
  const handleSaveChanges = async () => {
    try {
      if (oauthTokenHeader) {
        const data = {
          processes: updatedPayload?.processes || [], // Sequence's Processes
        };
        setLoading(true);
        await ETLSequencesEndPointLogic.editETLSequence(
          selectedSequenceId,
          oauthTokenHeader,
          data,
        );
        setSnackbarOpen(true);
        setSnackbarMessage(
          `Sequence was successfully updated and changes were deployed.`,
        );
        setChangesSaved(true);
        if (oauthTokenHeader) {
          setLoading(true);
          await ETLSequencesEndPointLogic.getETLSequenceContent(
            oauthTokenHeader,
            setEtlSequence,
            dispatch,
          );
        } else {
          throw new Error(
            "User doesn't have permissions to fetch ETL Sequence data",
          );
        }
      } else {
        throw new Error("User doesn't have permissions to edit ETL Sequence");
      }
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarMessage("Error updating sequence");
      setErrorAlert("Error updating sequence");
      console.error("Error while saving changes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevertChanges = async () => {
    try {
      if (oauthTokenHeader) {
        const data = {
          processes: processlist?.map((process: any) => process.process__id),
        };
        setLoading(true);
        await ETLSequencesEndPointLogic.editETLSequence(
          selectedSequenceId,
          oauthTokenHeader,
          data,
        );
        setSnackbarOpen(true);
        setSnackbarMessage(
          "The Changes have been reverted successfully to original state. Please make sure to Deploy & Sync the Sequence Configurations",
        );
        setChangesSaved(true);
        setFetchDataCalled(true);
        if (oauthTokenHeader) {
          setLoading(true);
          await ETLSequencesEndPointLogic.getETLSequenceContent(
            oauthTokenHeader,
            setEtlSequence,
            dispatch,
          );
        } else {
          throw new Error(
            "User doesn't have permissions to fetch ETL Sequence data",
          );
        }
      } else {
        throw new Error("User doesn't have permissions to edit ETL Sequence");
      }
    } catch (error) {
      setSnackbarOpen(true);
      setErrorAlert("Error updating sequence");
      setSnackbarMessage("Error reverting changes");
      console.error("Error while reverting changes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedSequenceId || !oauthTokenHeader) return;
    const fetchFailedLogs = async () => {
      setLoadingFL(true);
      try {
        await ETLSequencesEndPointLogic.getFailedProcessLogs(
          selectedSequenceId,
          oauthTokenHeader,
        );
      } finally {
        setLoadingFL(false);
      }
    };
    fetchFailedLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderSnackbar = () => {
    return (
      <Snackbar
        sx={{ zIndex: "9999999" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity="success"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  };

  return (
    <>
      {renderSnackbar()}
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Grid container spacing={2}>
          {errorAlert && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ marginBottom: "16px" }}>
                {errorAlert}
              </Alert>
            </Grid>
          )}
          <Grid item xs={4} sm={5} sx={{ px: 2 }}>
            <Typography variant="body1" mb={1}>
              Batch Process Management
            </Typography>
            <BatchProcessManagement
              processlist={processlist}
              onOrderChange={(updatedProcesses) => {
                setUpdatedPayload({
                  processes: updatedProcesses?.map(
                    (process: any) => process.process__id,
                  ),
                });
                handleProcessChange();
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4} sx={{ px: 2 }}>
            <Typography mb={1} variant="body1">
              Failed Processes
            </Typography>
            <FailedProcesses
              failedProcesses={failedProcesses}
              loading={loadingFL}
            />
          </Grid>
          <Grid item xs={3} sm={3} sx={{ px: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body1" sx={{ mr: "70px" }}>
                Sequence Status
              </Typography>
              <Switch
                color="success"
                checked={
                  processss?.active === false
                    ? isSwitchChecked
                    : !isSwitchChecked
                }
                onChange={() => handleSwitchChange(selectedSequenceId)}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                gap: 1,
              }}
            >
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                disabled={
                  changesSaved ||
                  originalProcesses.toString() ===
                    updatedPayload.processes.toString()
                }
                sx={{ fontSize: "13px", whiteSpace: "nowrap" }}
                onClick={handleRevertChanges}
              >
                REVERT Changes
              </Button>
              <Button
                size="small"
                color="secondary"
                variant="contained"
                disabled={
                  changesSaved ||
                  originalProcesses.toString() ===
                    updatedPayload.processes.toString()
                }
                sx={{ fontSize: "13px", whiteSpace: "nowrap" }}
                onClick={handleSaveChanges}
              >
                SAVE Changes
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default SequenceTableAdditionalContent;
